import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { Text } from '@/components/common/text';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import type { FaqSection } from '@/screens/residents/faq/faq';

interface FAQAccordionItemProps {
  section: FaqSection;
  index: number;
  testIdPrefix?: string;
  showSeparator?: boolean;
}

const ICON_SIZE = 24;

export const FAQAccordionItem: React.FC<FAQAccordionItemProps> = React.memo(({
  section,
  index,
  testIdPrefix = 'FAQ',
  showSeparator = true,
}) => {
  const { colors } = useThemeColors();
  const space = useSpacing();
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    <View style={[styles.container, showSeparator && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}>
      <Hoverable
        hover="row"
        bleed={{ x: space.sm, y: space.sm }}
        style={styles.header}
        accessibilityRole="button"
        onPress={toggle}
        testID={`${testIdPrefix}_Row_${index}_${expanded ? 'collapse' : 'expand'}`}
        accessibilityLabel={`${testIdPrefix}_Row_${index}`}
      >
        <Text style={[styles.title, { color: colors.text.primary }]}>{section.title}</Text>
        {expanded ? (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={ICON_SIZE} height={ICON_SIZE} color={colors.text.muted} />
        ) : (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={ICON_SIZE} height={ICON_SIZE} color={colors.text.muted} />
        )}
      </Hoverable>
      {expanded && <Text style={[styles.body, { color: colors.text.subtle }]}>{section.subtext}</Text>}
    </View>
  );
});

FAQAccordionItem.displayName = 'FAQAccordionItem';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    flex: 1,
  },
  body: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginTop: 10,
    lineHeight: 21,
  },
});
